import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import InnerLoader from "../../../component/ui/loader/inner-loader";
import { useApiCall } from "../../../component/custom-hooks/api-call";
import AudioPlayer from "../../../component/ui/audio-player";

import { useRef } from "react";
import { GlobalContext } from "../../../context/Context";
import { Edit3, Pause, Play } from "lucide-react";
import AddFullModal from "../StepForm/AddFullModal";
import { Alert, Snackbar } from "@mui/material";

const TranscriptDetail = () => {
    const { id, type } = useParams();
    const apiCall = useApiCall();
    let { state } = useContext(GlobalContext);


    const [data, setData] = useState(null);
    const [utterances, setUtterances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tags, setTags] = useState([]);
    const [activeTag, setActiveTag] = useState(null);

    const [audioSrc, setAudioSrc] = useState(null);
    const [headerPlaying, setHeaderPlaying] = useState(false);
    const [tagSearch, setTagSearch] = useState("");


    const utteranceRefs = useRef([]);

    const getTranscriptDetail = async () => {
        try {
            setLoading(true);

            const res = await apiCall({
                url: `view-transcript?id=${id}&type=${type}`,
                method: "get",
            });

            if (res?.success) {
                setData(res.data);
                setUtterances(res.utterances || []);
                setTags(res.tags || []);
            }
        } catch (error) {
            console.error("Transcript API Error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            getTranscriptDetail();
        }
    }, [id]);

    useEffect(() => {
        if (!activeTag) return;

        const index = utterances.findIndex(u =>
            u.text.toLowerCase().includes(activeTag.toLowerCase())
        );

        if (index !== -1 && utteranceRefs.current[index]) {
            utteranceRefs.current[index].scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [activeTag, utterances]);


    const getTagCount = (tag) => {
        if (!utterances.length) return 0;

        const regex = new RegExp(`\\b${tag}\\b`, "gi");

        return utterances.reduce((count, u) => {
            const matches = u.text.match(regex);
            return count + (matches ? matches.length : 0);
        }, 0);
    };

    const highlightText = (text) => {
        if (!activeTag) return text;

        const regex = new RegExp(`(${activeTag})`, "gi");

        return text.split(regex).map((part, index) =>
            part.toLowerCase() === activeTag.toLowerCase() ? (
                <mark
                    key={index}
                    className="bg-yellow-200 px-1 rounded"
                >
                    {part}
                </mark>
            ) : (
                part
            )
        );
    };

    const getSpeakerColor = (speaker) => {
        const colors = [
            "from-blue-100 to-blue-200",
            "from-green-100 to-green-200",
            "from-purple-100 to-purple-200",
            "from-pink-100 to-pink-200",
            "from-orange-100 to-orange-200",
            "from-teal-100 to-teal-200",
            "from-rose-100 to-rose-200",
            "from-indigo-100 to-indigo-200"
        ];

        const index = speaker.charCodeAt(0) - 65; // A=0, B=1...

        return colors[index % colors.length];
    };

    const highlightWord = (wordText) => {
        if (!activeTag) return false;

        return wordText
            .toLowerCase()
            .includes(activeTag.toLowerCase());
    };


    // const audioRef = useRef(null);
    const [currentTimeMs, setCurrentTimeMs] = useState(0);
    const [activeSentenceIndex, setActiveSentenceIndex] = useState(null);
    const [activeWordKey, setActiveWordKey] = useState(null);

    const [showAddModal, setAddModal] = useState(false);
    const [currentData, setCurrentData] = useState({});
    const [alertData, setAlertData] = useState({ isShow: false });


    const onSuccess = (message, id) => {
        setAlertData({ isShow: true, type: "success", message: message });
        setAddModal(false);
    };

    const onError = (message) => {
        setAlertData({ isShow: true, type: "error", message: message });
    };

    const editRefund = (ele) => {
        setCurrentData(ele);
        setAddModal(true)
    };
    const handleClose = () => {
        setAlertData({ isShow: false });
    };

    // useEffect(() => {
    //     const audio = audioRef.current;

    //     const onTimeUpdate = () => {
    //       const ms = audio.currentTime * 1000;
    //       setCurrentTimeMs(ms);
    //     };

    //     audio.addEventListener("timeupdate", onTimeUpdate);
    //     return () => audio.removeEventListener("timeupdate", onTimeUpdate);
    // }, []);

    useEffect(() => {
        utterances.forEach((sentence, sIndex) => {
            if (currentTimeMs >= sentence.start && currentTimeMs <= sentence.end) {
                setActiveSentenceIndex(sIndex);

                sentence.words.forEach((word, wIndex) => {
                    if (currentTimeMs >= word.start && currentTimeMs <= word.end) {
                        setActiveWordKey(`${sIndex}-${wIndex}`);
                    }
                });
            }
        });
    }, [currentTimeMs, utterances]);

    useEffect(() => {
        if (
            activeSentenceIndex !== -1 &&
            utteranceRefs.current[activeSentenceIndex]
        ) {
            utteranceRefs.current[activeSentenceIndex].scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [activeSentenceIndex]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6 w-[100%] h-[100vh] overflow-hidden flex flex-col">

            {/* Loader */}
            {loading && <InnerLoader />}

            {!loading && (
                <>
                    {/* Header Card */}
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6 flex justify-between items-center">
                        {/* Left Section: Info */}
                        <div className="flex flex-col gap-2">
                            <h1 className="text-2xl font-bold text-gray-900">Transcript</h1>
                            <div className="flex flex-wrap gap-4 mt-1 text-sm text-gray-600">
                                <div className="flex ">
                                    <span className="font-medium text-gray-500 uppercase tracking-wide">ID:</span>
                                    <span className="text-gray-800"> {data?.id}</span>
                                </div>
                                <div className="flex ">
                                    <span className="font-medium text-gray-500 uppercase tracking-wide">Merchant:</span>
                                    <span className="text-gray-800"> {data?.merchant}</span>
                                </div>
                                <div className="flex ">
                                    <span className="font-medium text-gray-500 uppercase tracking-wide">Time:</span>
                                    <span className="text-gray-800"> {data?.call_time}</span>
                                </div>

                                {data?.topic ? (
                                    <div className="flex ">
                                        <span className="font-medium text-gray-500 uppercase tracking-wide">TOPIC:</span>
                                        <span className="text-gray-800"> {data?.topic}</span>
                                    </div>
                                ) : null
                                }

                                {data?.host_email ? (
                                    <div className="flex ">
                                        <span className="font-medium text-gray-500 uppercase tracking-wide">Host Email:</span>
                                        <span className="text-gray-800"> {data?.host_email}</span>
                                    </div>
                                ) : null
                                }

                                {data?.meeting_uuid ? (
                                    <div className="flex ">
                                        <span className="font-medium text-gray-500 uppercase tracking-wide">Meeting UUID:</span>
                                        <span className="text-gray-800"> {data?.meeting_uuid}</span>
                                    </div>
                                ) : null
                                }


                            </div>
                        </div>

                        {/* Right Section: Buttons */}
                        <div className="flex gap-3 items-center">
                            {/* Edit Button */}
                            <button onClick={() => {
                                editRefund({ id: id, type: type });
                            }} className="w-11 h-11 rounded-full border border-gray-300 hover:bg-gray-100 transition flex items-center justify-center shadow-sm">
                                <Edit3 size={20} className="text-gray-700" />
                            </button>

                            {/* Play / Pause Button */}
                            <button
                                onClick={() => {
                                    const audioEl = document.querySelector("audio");
                                    if (!audioSrc) {
                                        setAudioSrc(`${state.baseUrl}/play-recording/${type}/${id}`);
                                        setHeaderPlaying(true);
                                    } else if (audioEl) {
                                        if (headerPlaying) {
                                            audioEl.pause();
                                            setHeaderPlaying(false);
                                        } else {
                                            audioEl.play();
                                            setHeaderPlaying(true);
                                        }
                                    }
                                }}
                                className={`w-11 h-11 rounded-full text-white transition flex items-center justify-center shadow
        ${headerPlaying ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}
      `}
                            >
                                {headerPlaying ? <Pause size={22} /> : <Play size={22} />}
                            </button>
                        </div>
                    </div>


                    {/* Main Layout */}
                    <div className="grid grid-cols-12 gap-6 w-[100%] h-[100%] overflow-hidden">

                        {/* Transcript */}
                        <div className="col-span-9 bg-white rounded-xl shadow-sm border border-gray-200 p-5 h-[100%] overflow-hidden flex flex-col">
                            <h2 className="font-semibold text-gray-800 mb-4">
                                Conversation
                            </h2>

                            <div className="h-[100%] overflow-y-auto space-y-4 pr-2 scrollbar-thin scrollbar-thumb-gray-300">
                                {utterances.map((item, index) => {
                                    const isMatched =
                                        activeTag &&
                                        item.text.toLowerCase().includes(activeTag.toLowerCase());

                                    const isActive = index === activeSentenceIndex;

                                    return (
                                        <div
                                            key={index}
                                            ref={(el) => (utteranceRefs.current[index] = el)}
                                            className={`flex gap-3 items-start transition-all
                                                ${isActive ? "bg-blue-50 rounded-lg p-2" : ""
                                                }
                                            `}
                                        >
                                            {/* Speaker Avatar */}
                                            <div
                                                className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center 
                                                text-black font-semibold bg-gradient-to-br ${getSpeakerColor(item.speaker)}`}
                                            >
                                                {item.speaker}
                                            </div>

                                            {console.log("item", item)}

                                            {/* Message Bubble */}
                                            <div className={`bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-800 leading-relaxed
                                                
                                                ${isMatched
                                                    ? " border-[#0B54FF]"
                                                    : ""
                                                }
                                                
                                                `}>
                                                {highlightText(item.text)}
                                            </div>

                                            {/* <div
                                                className={`border rounded-lg px-4 py-2 text-sm leading-relaxed transition-all
                                                    ${isActive
                                                        ? "bg-white border-blue-500 shadow-sm"
                                                        : "bg-gray-50 border-gray-200"
                                                    }
                                                    ${isMatched ? "border-[#0B54FF]" : ""}
                                                `}
                                            >
                                                <div className="flex flex-wrap gap-1">
                                                    {item.words.map((word, wIndex) => {
                                                        const isWordActive =
                                                        currentTimeMs >= word.start &&
                                                        currentTimeMs <= word.end;

                                                        const isTagMatched = highlightWord(word.text);

                                                        return (
                                                            <span
                                                            key={wIndex}
                                                            className={`px-1 rounded transition-all
                                                                ${
                                                                isWordActive
                                                                    ? "bg-blue-600 text-white"
                                                                    : isTagMatched
                                                                    ? "bg-yellow-200"
                                                                    : "text-gray-800"
                                                                }
                                                            `}
                                                            >
                                                            {word.text}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            </div> */}
                                        </div>
                                    );
                                })}
                            </div>

                            {audioSrc && (
                                <div className="mt-4">
                                    <AudioPlayer
                                        src={audioSrc}
                                        onClose={() => {
                                            setAudioSrc(null);
                                            setHeaderPlaying(false);
                                        }}
                                        onPlayChange={(playing) => setHeaderPlaying(playing)}
                                        onTimeUpdate={(ms) => setCurrentTimeMs(ms)}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Tags */}
                        <div className="col-span-3 bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col">
                            {/* Header: Title + Search */}
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="font-semibold text-gray-800">Tags</h2>

                                {/* Search Input */}
                                <input
                                    type="text"
                                    placeholder="Search tags..."
                                    value={tagSearch}
                                    onChange={(e) => setTagSearch(e.target.value)}
                                    className="px-3 py-1.5 border rounded-md text-xs w-32 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                />
                            </div>

                            {/* Tags Grid */}
                            <div className="flex flex-wrap gap-2">
                                {tags
                                    .filter(tag => tag.toLowerCase().includes(tagSearch.toLowerCase()))
                                    .map((tag, index) => {
                                        const count = getTagCount(tag);
                                        const isDisabled = count === 0;
                                        const isActive = activeTag === tag;

                                        return (
                                            <button
                                                key={index}
                                                disabled={isDisabled}
                                                onClick={() => !isDisabled && setActiveTag(isActive ? null : tag)}
                                                className={`px-3 py-1.5 rounded-full border text-xs flex items-center gap-1 transition-all
              ${isDisabled
                                                        ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                                        : isActive
                                                            ? "bg-blue-600 text-white border-blue-600 shadow"
                                                            : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                                                    }
            `}
                                                title={isDisabled ? "No match found in transcript" : ""}
                                            >
                                                {tag}

                                                {!isDisabled && (
                                                    <span
                                                        className={`px-2 py-[1px] rounded-full text-[10px] font-semibold
                  ${isActive
                                                                ? "bg-white text-blue-600"
                                                                : "bg-blue-200 text-blue-800"
                                                            }
                `}
                                                    >
                                                        {count}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                            </div>
                        </div>

                    </div>



                    {
                        showAddModal && currentData !== "" && (
                            <AddFullModal
                                callId={currentData?.type == "meeting" ? data?.meeting_uuid : currentData?.id}
                                type={currentData?.type}
                                onError={onError}
                                onSubmit={onSuccess}
                                onClose={() => {
                                    setAddModal(false);
                                    setCurrentData({});
                                }}
                            />
                        )
                    }

                    <Snackbar
                        open={alertData?.isShow}
                        autoHideDuration={3000}
                        onClose={handleClose}
                    >
                        <Alert
                            onClose={handleClose}
                            severity={alertData?.type}
                            variant="filled"
                            sx={{ width: "100%" }}
                        >
                            {alertData?.message}
                        </Alert>
                    </Snackbar>
                </>
            )
            }
        </div >
    );
};

export default TranscriptDetail;
